import { IHttpService } from "../HttpService";
import RuleGroup, { IRuleGroup } from "../../model/evaluation/RuleGroup";
import BusinessRule from "../../model/evaluation/BusinessRule";
import RuleGroupElement, {
  IRuleGroupElement
} from "../../model/evaluation/RuleGroupElement";

export interface IRuleGroupService {
  getRuleGroups(): Promise<IRuleGroup[]>;
  createRuleGroup(ruleGroup: IRuleGroup): Promise<any>;
  updateRuleGroup(ruleGroup: IRuleGroup): void;
  getRuleGroupElements(ruleGroupId: number): Promise<IRuleGroupElement[]>;
  addRuleToRuleGroup(
    ruleGroup: IRuleGroup,
    ruleGroupElement: IRuleGroupElement
  ): Promise<any>;
  removeRuleFromRuleGroup(
    ruleGroup: IRuleGroup,
    ruleGroupElement: IRuleGroupElement
  ): Promise<any>;
  updateRuleFromRuleGroup(
    ruleGroup: IRuleGroup,
    ruleGroupElement: IRuleGroupElement
  );
}

const RuleGroupService = (httpService): IRuleGroupService => {
  const _http: IHttpService = httpService;
  const _baseUrl: string = "/api/";
  const _basePath = "business-rules-groups";
  const _elementsPathPart = "elements";

  const RuleGroupMapper = (json = []) => {
    return json.map(RuleGroup);
  };

  const RuleGroupElementMapper = json => {
    return (json || []).map(item => {
      return RuleGroupElement({
        id: item.id,
        displayNumber: item.number,
        weightFactor: item.weightFactor,
        businessRule: BusinessRule({
          id: item.businessRuleId,
          name: item.businessRuleName,
          description: item.businessRuleDescription,
          rule: ""
        })
      });
    });
  };

  return {
    async getRuleGroups() {
      const path = _http.buildPath(_baseUrl, _basePath);
      const response = await _http.get(path);
      const responseJSON = await _http.toJSON(response);
      return RuleGroupMapper(responseJSON);
    },

    async createRuleGroup(ruleGroup: IRuleGroup) {
      const path = _http.buildPath(_baseUrl, _basePath);
      const response = await _http.post(path, {
        params: ruleGroup,
        form: true
      });
      return _http.toText(response);
    },

    async updateRuleGroup(ruleGroup: IRuleGroup) {
      const path = _http.buildPath(
        _baseUrl,
        _basePath,
        ruleGroup.id.toString()
      );
      const response = await _http.put(path, {
        params: {
          id: ruleGroup.id,
          name: ruleGroup.name,
          description: ruleGroup.description,
          weightThreshold: ruleGroup.weightThreshold
        },
        form: true
      });
      return _http.toText(response);
    },

    async getRuleGroupElements(ruleGroupId: number) {
      const path = _http.buildPath(
        _baseUrl,
        _basePath,
        ruleGroupId.toString(),
        _elementsPathPart
      );
      const response = await _http.get(path);
      const responseJSON = await _http.toJSON(response);
      return RuleGroupElementMapper(responseJSON);
    },

    async addRuleToRuleGroup(
      ruleGroup: IRuleGroup,
      ruleGroupElement: IRuleGroupElement
    ) {
      const path = _http.buildPath(
        _baseUrl,
        _basePath,
        ruleGroup.id.toString(),
        _elementsPathPart
      );
      const response = await _http.post(path, {
        params: {
          businessRuleId: ruleGroupElement.businessRule.id,
          weightFactor: ruleGroupElement.weightFactor || 0,
          number: ruleGroupElement.displayNumber || 0
        },
        form: true
      });
      return _http.toText(response);
    },

    async removeRuleFromRuleGroup(
      ruleGroup: IRuleGroup,
      ruleGroupElement: IRuleGroupElement
    ) {
      const path = _http.buildPath(
        _baseUrl,
        _basePath,
        ruleGroup.id.toString(),
        _elementsPathPart,
        ruleGroupElement.id.toString()
      );
      const response = await _http.remove(path, {
        form: true
      });
      return _http.toText(response);
    },
    async updateRuleFromRuleGroup(
      ruleGroup: IRuleGroup,
      ruleGroupElement: IRuleGroupElement
    ) {
      const path = _http.buildPath(
        _baseUrl,
        _basePath,
        ruleGroup.id.toString(),
        _elementsPathPart,
        ruleGroupElement.id.toString()
      );
      const response = await _http.put(path, {
        params: {
          weightFactor: ruleGroupElement.weightFactor || 0,
          orderNumber: ruleGroupElement.displayNumber || 0
        },
        form: true
      });
      return _http.toText(response);
    }
  };
};

export default RuleGroupService;
